import { useLocalization } from '@lib/localization';
import SPProject from '@lib/spproject/SPProject';
import { Close, CloudUpload, Info, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { type IFolder } from '@store/api/folderEndpoints';
import { type IInspection } from '@store/api/inspectionEndpoints';
import { type PartialProjectFolderInfo, useLazyGetProjectQuery } from '@store/api/projectEndpoints';
import { useAppDispatch } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { useCallback, useMemo, useState } from 'react';
import { UploadIcon } from 'react-line-awesome';
import { useParams } from 'react-router-dom';
import {
  DialogHeader,
  Dropzone,
  FileInfoBox,
  FileInfoGrid,
  HeaderTitleBox,
  InfoLabel,
  InfoValue,
  InspectionItem,
  SearchContainer,
  SectionHeader,
  SectionTitle,
  SelectAllContainer,
  StyledAlert,
  StyledChip,
  UploadCircle,
} from './UploadModal.styled';

interface ProjectUploadModalProps {
  open: boolean;
  onClose: () => void;
  uploadProject: (formData: FormData, id?: string) => Promise<void>;
  folder?: IFolder | null;
  existingInspections?: IInspection[];
  onBulkUploadRequested?: () => void;
}

const ProjectUploadModal: React.FC<ProjectUploadModalProps> = ({
  open, onClose, uploadProject, folder, existingInspections, onBulkUploadRequested
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [spproject, setSPProject] = useState<SPProject | null>(null);
  const [selectedInspectionIds, setSelectedInspectionIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [fetchProject] = useLazyGetProjectQuery();
  const dispatch = useAppDispatch();

  const { projectId } = useParams();
  const { t } = useLocalization();

  const filteredInspections = useMemo(() => {
    if (!searchQuery.trim()) return spproject?.inspections ?? [];

    const q = searchQuery.toLowerCase();

    return spproject?.inspections.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        i.id.toLowerCase().includes(q) ||
        i.baseTemplateId?.toLowerCase().includes(q)
    ) ?? [];
  }, [searchQuery, spproject?.inspections]);

  const allFilteredSelected = useMemo(
    () => filteredInspections.length > 0 && filteredInspections.every((i) => selectedInspectionIds.has(i.id)),
    [filteredInspections, selectedInspectionIds]
  );

  const handleProcessFile = useCallback(
    async (file: File) => {
      try {
        if (!file.name.toLowerCase().endsWith('.spproject')) return setError('Please select a .spproject file');

        setLoading(true);
        setError(null);

        setSelectedFile(file);

        const spproject = await SPProject.loadAsync(file);
        if (projectId && spproject.manifest.projectInfo.id != projectId) {
          return setError('Selected .spproject file doesn\'t match with this project.');
        } else {
          setSPProject(spproject);
        }

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error occurred while processing file');
      } finally {
        setLoading(false);
      }
    }, [projectId]
  );

  const handleFileSelect = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.spproject';

    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) handleProcessFile(file);
    };

    input.click();
  }, [handleProcessFile]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      setDragOver(false);

      const file = e.dataTransfer.files?.[0];
      if (file) handleProcessFile(file);
    },
    [handleProcessFile]
  );

  const handleUpload = async () => {
    if (!selectedFile) return;

    if (!projectId) {
      const existingProjectResponse = spproject ? await fetchProject(spproject?.manifest.projectInfo.id) : null;

      if (existingProjectResponse?.isSuccess) {
        const projectInfo = existingProjectResponse.data;
        const projectParentFolderPath = (projectInfo.parentFolder as PartialProjectFolderInfo)?.path ?? '';
        const projectPath = `${projectParentFolderPath}/${projectInfo.projectInfo.name}.spproject`;
        const message = t('Project_Form_AlreadyExist_Error', [projectPath]);

        return dispatch(showSnackbar({ severity: 'error', message }));
      };
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('inspectionIds', JSON.stringify(Array.from(selectedInspectionIds)));
    if (folder?._id) formData.append('parentFolder', folder._id);

    uploadProject(formData);

    handleClose();
  };

  const handleInspectionToggle = (id: string) => {
    setSelectedInspectionIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = filteredInspections.map((i) => i.id);
    const allSelected = allIds.every((id) => selectedInspectionIds.has(id));

    setSelectedInspectionIds((prev) => {
      const next = new Set(prev);

      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [onClose]);

  const handleReset = () => {
    setSelectedFile(null);
    setSPProject(null);
    setSelectedInspectionIds(new Set());
    setSearchQuery('');
    setError(null);
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <DialogHeader>
          <HeaderTitleBox>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {!selectedFile
                  ? t('Project_Form_Title') :
                  t(`Project_Form_${!projectId ? 'Add' : 'Update'}`)}
              </Typography>
              {!selectedFile && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('Project_Form_Subtitle', ['.spproject'])}
                </Typography>
              )}
            </Box>
          </HeaderTitleBox>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </DialogHeader>
      </DialogTitle>

      <DialogContent>
        {!selectedFile ? (
          <Dropzone
            dragOver={dragOver}
            onClick={handleFileSelect}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
          >
            <UploadCircle>
              <CloudUpload sx={{ fontSize: 40, color: 'white' }} />
            </UploadCircle>
            <Typography variant="h6" fontWeight={600}>
              {t('Project_Form_DragDrop_Title', ['.spproject'])}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Project_Form_DragDrop_Subtitle', ['.spproject'])}
            </Typography>
          </Dropzone>
        ) : loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
            <CircularProgress size={48} sx={{ color: '#059669' }} />
            <Typography variant="h6" fontWeight={500}>
              Processing file...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we analyze your project file
            </Typography>
          </Box>
        ) : error ? (
          <StyledAlert severity="error">
            <Typography fontWeight={500}>{error}</Typography>
          </StyledAlert>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            <Box>
              <SectionHeader>
                <SectionTitle>{t('Project_Information')}</SectionTitle>
              </SectionHeader>
              <FileInfoGrid>
                <FileInfoBox>
                  <InfoLabel>{t('File_Name')}</InfoLabel>
                  <InfoValue>{selectedFile?.name}</InfoValue>
                </FileInfoBox>
                <FileInfoBox>
                  <InfoLabel>{t('Project_Name')}</InfoLabel>
                  <InfoValue>{spproject?.manifest.projectInfo.name ?? '-'}</InfoValue>
                </FileInfoBox>
                <FileInfoBox>
                  <InfoLabel>{t('Project_ID')}</InfoLabel>
                  <InfoValue>{spproject?.manifest.projectInfo.id ?? '-'}</InfoValue>
                </FileInfoBox>
                <FileInfoBox>
                  <InfoLabel>{t('Common_DateCreated')}</InfoLabel>
                  <InfoValue>
                    {spproject?.manifest.projectInfo.dateCreated
                      ? new Date(spproject.manifest.projectInfo.dateCreated).toAgoString()
                      : '-'}
                  </InfoValue>
                </FileInfoBox>
                <FileInfoBox>
                  <InfoLabel>{t('Common_DateModified')}</InfoLabel>
                  <InfoValue>
                    {spproject?.manifest.projectInfo.dateModified
                      ? new Date(spproject.manifest.projectInfo.dateModified).toAgoString()
                      : '-'}
                  </InfoValue>
                </FileInfoBox>
                <FileInfoBox>
                  <InfoLabel>{t('Common_Version')}</InfoLabel>
                  <InfoValue>{spproject?.manifest.projectInfo.version ?? '-'}</InfoValue>
                </FileInfoBox>
              </FileInfoGrid>
            </Box>

            <Box>
              <SectionHeader>
                <SectionTitle>{t('Common_Inspections')} ({spproject?.inspections.length})</SectionTitle>
                {spproject?.inspections.length && (
                  <SelectAllContainer>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allFilteredSelected}
                          indeterminate={
                            !allFilteredSelected &&
                            filteredInspections.some((i) => selectedInspectionIds.has(i.id))
                          }
                          onChange={handleSelectAll}
                        />
                      }
                      label={
                        selectedInspectionIds.size === 0
                          ? t('Project_Form_Inspections_SelectAll')
                          : t('Project_Form_Inspections_Select_Count', { count: selectedInspectionIds.size })
                      }
                    />
                  </SelectAllContainer>
                )}
              </SectionHeader>

              {!spproject?.inspections.length ? (
                <StyledAlert severity="info" icon={<Info />}>
                  <Typography>{t('Project_Form_Inspections_NotFound', ['.spproject'])}</Typography>
                </StyledAlert>
              ) : (
                <>
                  <SearchContainer>
                    <TextField
                      fullWidth
                      placeholder={t('Project_Inspections_Search')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                    />
                  </SearchContainer>

                  <List dense disablePadding>
                    {filteredInspections.map((i) => {
                      const isExisting = existingInspections?.some(inspection => inspection.info.id == i.id);

                      return (
                        <InspectionItem key={i.id} onClick={() => handleInspectionToggle(i.id)}>
                          <Checkbox checked={selectedInspectionIds.has(i.id)} sx={{ mr: 2 }} />
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight={500} color="text.primary">
                                {i.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {i.id}
                              </Typography>
                            }
                          />
                          {isExisting && <StyledChip label={'Already Exist'} color="primary" size="small"
                            variant="outlined" sx={{ mr: 1 }} />}
                          <StyledChip
                            label={new Date(i.dateCreated).toAgoString()}
                            size="small"
                            variant="outlined"
                          />
                        </InspectionItem>
                      );
                    })}
                  </List>

                  {searchQuery && filteredInspections.length === 0 && (
                    <Box textAlign="center" py={3}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Inspections_NotMatch')}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        {!projectId && onBulkUploadRequested ? (
          <Button
            onClick={onBulkUploadRequested}
            startIcon={<UploadIcon />}
            color="inherit"
          >
            {t('Project_Bulk_Button')}
          </Button>
        ) : <Box />}
        <Box display="flex" gap={1}>
          <Button onClick={handleClose}>{t('Common_Cancel')}</Button>
          {(selectedFile && !error) && (
            <Button variant="contained" onClick={handleUpload}>
              {t('Project_Form_UploadProject', { count: selectedInspectionIds.size })}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectUploadModal;
